import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { TeamBadge } from "@/components/TeamBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Shield, Check } from "lucide-react";
import { toast } from "sonner";

export function FavoriteClubControl({ user, updateUser, onChanged }) {
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if ((open || user?.favorite_team_id) && teams.length === 0) {
      api.get("/teams").then(({ data }) => setTeams(data.teams)).catch(() => {});
    }
  }, [open, user?.favorite_team_id, teams.length]);

  const favId = user?.favorite_team_id;
  const current = useMemo(() => teams.find((t) => t.id === favId), [teams, favId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term ? teams.filter((t) => t.name.toLowerCase().includes(term) || (t.short || "").toLowerCase().includes(term)) : teams;
    return list.reduce((acc, tm) => {
      (acc[tm.league_name] = acc[tm.league_name] || { emoji: tm.league_emoji, items: [] }).items.push(tm);
      return acc;
    }, {});
  }, [teams, q]);

  const pick = async (tm) => {
    if (busy || tm.id === favId) { setOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/me/favorite-team", { team_id: tm.id });
      updateUser({ favorite_team_id: data.team_id, favorite_league_id: data.league_id });
      onChanged?.(data.team_id, data.league_id, data.name);
      toast.success(`Your club is now ${data.name}.`);
      setOpen(false);
      setQ("");
    } catch {
      toast.error("Couldn't change your club. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="fav-club-chip"
          title="Change your favourite club"
          className="inline-flex items-center gap-2 px-2.5 py-1 border border-border hover:border-primary/60 transition-colors max-w-[160px]">
          {current ? (
            <>
              <TeamBadge team={current} size={16} />
              <span className="hidden sm:inline text-xs font-semibold truncate">{current.short || current.name}</span>
            </>
          ) : (
            <>
              <Shield size={14} className="text-muted-foreground" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Pick club</span>
            </>
          )}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase">Your club</DialogTitle>
          <DialogDescription className="font-data text-xs">
            Pick the club you support — we'll tailor your news, highlighted fixtures and more.
          </DialogDescription>
        </DialogHeader>
        <Input autoFocus placeholder="Search clubs…" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="fav-club-search" className="rounded-none border-2" />
        <div className="max-h-[55vh] overflow-y-auto border-t-2 border-foreground" data-testid="fav-club-list">
          {Object.entries(filtered).map(([lname, g]) => (
            <div key={lname}>
              <p className="sticky top-0 bg-card px-3 py-1.5 font-data text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">{g.emoji} {lname}</p>
              {g.items.map((tm) => (
                <button key={tm.id} onClick={() => pick(tm)} disabled={busy}
                  data-testid={`fav-club-opt-${tm.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-border text-left transition-colors ${tm.id === favId ? "bg-primary/15" : "hover:bg-primary/[0.06]"}`}>
                  <TeamBadge team={tm} size={20} />
                  <span className="flex-1 text-sm font-medium truncate">{tm.name}</span>
                  {tm.id === favId && <Check size={15} className="text-primary shrink-0" />}
                </button>
              ))}
            </div>
          ))}
          {Object.keys(filtered).length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">No clubs match "{q}".</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
